import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { arrowRightIcon, checkCircleIcon, clockIcon, microphoneIcon, SVGIcon, warningTriangleIcon, xCircleIcon } from "@progress/kendo-svg-icons";
import { useEffect, useRef, useState } from "react";
import { Command } from "TypeGen/Driver/ReachabilityCheck/command";
import { DriverReachabilityCheckStatus } from "TypeGen/Driver/ReachabilityCheck/driver-reachability-check-status";
import { HistoryItem } from "TypeGen/Driver/ReachabilityCheck/history-item";
import { ViewModel } from "TypeGen/Driver/ReachabilityCheck/view-model";
import { JsonResponse } from "TypeGen/json-response";
import { formatPhone } from "../../utils/utils";
import { fetchApi } from "../../services/api";

type Props = {
  driverId: number;
  onClose: () => void;
};

// +1 (734) 442-2232 is disabled for now: SIP is not established on that number yet.
const FROM_NUMBERS = [
  { label: "+1 (734) 442-2238", value: "+17344422238" },
];

const STATUS_INFO: Record<DriverReachabilityCheckStatus, { label: string; color: string; icon: SVGIcon }> = {
  [DriverReachabilityCheckStatus.Placed]: { label: "Calling...", color: "#6c757d", icon: clockIcon },
  [DriverReachabilityCheckStatus.Connected]: { label: "Connected", color: "#28a745", icon: checkCircleIcon },
  [DriverReachabilityCheckStatus.Voicemail]: { label: "Went to voicemail", color: "#fd7e14", icon: microphoneIcon },
  [DriverReachabilityCheckStatus.NoAnswer]: { label: "No answer", color: "#dc3545", icon: xCircleIcon },
  [DriverReachabilityCheckStatus.Failed]: { label: "Call failed", color: "#dc3545", icon: warningTriangleIcon },
};

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120000;

const formatDuration = (ms: number) => {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const formatRelativeDate = (iso: string) => {
  const date = new Date(iso);
  const dayMs = 24 * 60 * 60 * 1000;
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === new Date(today.getTime() - dayMs).toDateString();
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (isToday) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;
  return `${date.toLocaleDateString()}, ${time}`;
};

const DriverReachabilityCheckDialog: React.FC<Props> = ({ driverId, onClose }) => {
  const [fromNumber, setFromNumber] = useState(FROM_NUMBERS[0]);
  const [calling, setCalling] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [timedOut, setTimedOut] = useState(false);
  const pollRef = useRef<number>();
  const pollStartedAt = useRef(0);
  const requestIdRef = useRef(0);

  const stopPolling = () => window.clearInterval(pollRef.current);

  // Tags each fetch with a sequence number so a slow, older request can never overwrite the
  // result of a newer one that already resolved.
  const loadHistory = () => {
    const requestId = ++requestIdRef.current;
    return fetchApi(`/api/Asset/GetDriverReachabilityCheckHistory?driverId=${driverId}`)
      .then((vm: ViewModel) => ({ requestId, items: vm.History ?? [] }));
  };

  useEffect(() => {
    loadHistory().then(({ requestId, items }) => {
      if (requestId === requestIdRef.current) setHistory(items);
    });
    return stopPolling;
  }, [driverId]);

  const poll = () => {
    loadHistory()
      .then(({ requestId, items }) => {
        if (requestId !== requestIdRef.current) return;

        if (items[0] && items[0].Status !== DriverReachabilityCheckStatus.Placed) {
          stopPolling();
          setHistory(items);
          setCalling(false);
          return;
        }

        if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
          stopPolling();
          setCalling(false);
          setTimedOut(true);
        }
      })
      .catch(() => {
        stopPolling();
        setCalling(false);
        alert('Lost contact while waiting for the call result.');
      });
  };

  const place = () => {
    stopPolling();
    setCalling(true);
    setTimedOut(false);

    fetchApi('/api/Asset/PlaceDriverReachabilityCheckCall', { DriverID: driverId, FromNumber: fromNumber.value } as Command, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success === false) {
          alert(response.ErrorMessage);
          setCalling(false);
          return;
        }

        pollStartedAt.current = Date.now();
        pollRef.current = window.setInterval(poll, POLL_INTERVAL_MS);
      })
      .catch(() => {
        alert('Unable to place the test call.');
        setCalling(false);
      });
  };

  const latest = history[0];
  const lastSuccess = history.find((h) => h.Status === DriverReachabilityCheckStatus.Connected);
  const statusInfo = calling ? STATUS_INFO[DriverReachabilityCheckStatus.Placed] : (latest ? STATUS_INFO[latest.Status] : null);

  return <Dialog title="Test Driver Reachability" onClose={onClose} width={560}>
    <div className="p-3">
      <div className="form-group row align-items-center">
        <label className="col-sm-4 col-form-label">From Number</label>
        <div className="col-sm-8">
          <DropDownList
            data={FROM_NUMBERS}
            textField="label"
            dataItemKey="value"
            value={fromNumber}
            onChange={(e) => setFromNumber(e.value)}
            disabled={calling}
          />
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-center my-4" style={{ gap: '0.75rem' }}>
        <div className="text-center">
          <div className="text-muted" style={{ fontSize: '11px' }}>FROM</div>
          <div style={{ fontWeight: 600 }}>{formatPhone(fromNumber.value.replace(/\D/g, ''))}</div>
        </div>
        <SvgIcon icon={arrowRightIcon} className="text-muted" style={{ marginTop: '10px' }} />
        <div className="text-center">
          <div className="text-muted" style={{ fontSize: '11px' }}>TO</div>
          <div style={{ fontWeight: 600 }}>{latest ? formatPhone(latest.ToNumber.replace(/\D/g, '')) : 'Driver\'s cell'}</div>
        </div>
      </div>

      {statusInfo && <div className="d-flex align-items-center justify-content-center mb-1" style={{ gap: '0.5rem', color: statusInfo.color }}>
        <SvgIcon icon={statusInfo.icon} />
        <strong>{statusInfo.label}</strong>
      </div>}

      {timedOut && <div className="text-muted text-center mb-2">No result after 2 minutes. Try again, or check back later.</div>}

      {lastSuccess && lastSuccess !== latest && <div className="text-muted text-center mb-2" style={{ fontSize: '12px' }}>
        Last reached successfully {formatRelativeDate(lastSuccess.CreatedDateTime)}
      </div>}

      {history.length > 0 && <div className="border-top pt-2 mt-2">
        <div className="text-muted" style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.03em' }}>CALL HISTORY</div>
        <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
          {history.map((item, i) => {
            const info = STATUS_INFO[item.Status];
            return <div key={i} className="d-flex align-items-center py-1" style={{ fontSize: '12px', gap: '0.5rem', borderBottom: i < history.length - 1 ? '1px solid #f0f0f0' : undefined }}>
              <SvgIcon icon={info.icon} style={{ color: info.color, flexShrink: 0 }} size="small" />
              <span style={{ width: '130px', flexShrink: 0 }}>{formatRelativeDate(item.CreatedDateTime)}</span>
              <span style={{ color: info.color, width: '110px', flexShrink: 0 }}>{info.label}</span>
              <span className="text-muted" style={{ flexShrink: 0 }}>{item.DurationMs != null ? formatDuration(item.DurationMs) : ''}</span>
              <span className="text-muted text-truncate">{item.DisconnectionReason ?? ''}</span>
            </div>;
          })}
        </div>
      </div>}
    </div>
    <DialogActionsBar>
      <Button onClick={onClose}>Close</Button>
      <Button themeColor="primary" disabled={calling} onClick={place}>
        {history.length > 0 ? 'Call Again' : 'Call'}
      </Button>
    </DialogActionsBar>
  </Dialog>;
};

export default DriverReachabilityCheckDialog;
